import React, { useEffect, useState } from "react";
import TableHeader from "../Assets/TableHeader";
import TableBody from "../Assets/TableBody";
import { AboutUsEntry } from "../Types/Types";
import LoadingCircle from "../Assets/LoadingCirlce";
import TableFooterPagination from "../Assets/TableFooterPagination"; // Import renamed pagination footer

interface AdminAboutUsTableProps {
  aboutUsData: AboutUsEntry[];
  loading: boolean;
  error: string | null;
  onDelete: (id: string) => void;
  onEdit: (entry: AboutUsEntry) => void;
  onSortChange: (field: string, order: string) => void;
  lang: string;
  onReorder?: (newOrder: AboutUsEntry[]) => Promise<void>;
  success: string | null;
  setSuccess: (message: string | null) => void;  // <-- add this line

}

const AdminAboutUsTable: React.FC<AdminAboutUsTableProps> = ({
  aboutUsData,
  loading,
  error,
  onDelete,
  onEdit,
  onSortChange,
  lang,
  onReorder,
  success,
  setSuccess
}) => {
  const [sortBy, setSortBy] = useState<string>("createdAt");
  const [sortOrder, setSortOrder] = useState<string>("desc");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(5);
  const [localData, setLocalData] = useState<AboutUsEntry[]>(aboutUsData);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    setLocalData(aboutUsData);
  }, [aboutUsData]);

  useEffect(() => {
    if (success) {
      setShowSuccess(true);
      const timer = setTimeout(() => {
        setShowSuccess(false);
        setSuccess(null); // clear success after 3 sec
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success, setSuccess]);
  // Pagination logic
  const totalPages = Math.ceil(aboutUsData.length / rowsPerPage);
  const indexOfLast = currentPage * rowsPerPage;
  const indexOfFirst = indexOfLast - rowsPerPage;
  const currentData = aboutUsData.slice(indexOfFirst, indexOfLast);

  const handleSort = (column: string) => {
    const newOrder = sortBy === column && sortOrder === "asc" ? "desc" : "asc";
    setSortBy(column);
    setSortOrder(newOrder);
    onSortChange(column, newOrder);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newRows = parseInt(e.target.value, 10);
    setRowsPerPage(newRows);
    setCurrentPage(1); // Reset to first page when changing rows per page
  };

  const handleReorder = async (reorderedCurrentPageData: AboutUsEntry[]) => {
    const newFullOrder = [
      ...localData.slice(0, indexOfFirst),
      ...reorderedCurrentPageData,
      ...localData.slice(indexOfLast),
    ];
    setLocalData(newFullOrder);
    if (onReorder) {
      await onReorder(newFullOrder);
    }
  };

  if (loading) return <LoadingCircle />;
  if (error) return <div className="text-center py-4 text-red-500">{error}</div>;

  return (
    <div className="overflow-x-auto bg-white shadow-md rounded-lg">
      {showSuccess && (
        <div className="absolute left-1/2 bottom-4 transform -translate-x-1/2 bg-green-600 text-white px-6 py-2 rounded-full shadow-lg text-lg font-semibold select-none">
          {success}
        </div>
      )}
      <table className="min-w-full table-auto">
        <TableHeader
          data={aboutUsData}
          handleSort={handleSort}
          sortBy={sortBy}
          sortOrder={sortOrder}
        />
        <TableBody
          data={currentData}  // Display current page data
          onDelete={onDelete}
          onEdit={onEdit}
          lang={lang}
          onReorder={handleReorder}
          editable={true}

        />
      </table>

      {/* Table footer with pagination */}
      <TableFooterPagination
        currentPage={currentPage}
        totalPages={totalPages}
        rowsPerPage={rowsPerPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />
    </div>
  );
};

export default AdminAboutUsTable;

